import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Booking } from '@shared/types';

export default function BookingConfirmation() {
  const { id } = useParams<{ id: string }>();

  const { data: booking } = useQuery({
    queryKey: ['booking', id],
    queryFn: async () => {
      if (!id) return null;
      const docRef = doc(db, 'bookings', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          checkinTimestamp: data.checkinTimestamp instanceof Timestamp ? data.checkinTimestamp.toDate() : data.checkinTimestamp,
          checkoutTimestamp: data.checkoutTimestamp instanceof Timestamp ? data.checkoutTimestamp.toDate() : data.checkoutTimestamp,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        } as Booking;
      }
      return null;
    },
  });

  if (!booking) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-gray-600">Booking not found</p>
      </div>
    );
  }

  const checkIn = booking.checkinTimestamp.toLocaleDateString('en-ZA');
  const checkOut = booking.checkoutTimestamp.toLocaleDateString('en-ZA');
  const total = (booking.totalCents / 100).toFixed(2);
  const isPending = booking.status === 'pending';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">{isPending ? '📩' : '✓'}</div>
        <h1 className="text-3xl font-bold mb-2">
          {isPending ? 'Request received' : 'Booking confirmed!'}
        </h1>
        <p className="text-gray-600">
          {isPending
            ? 'Thank you. We will get back to you within the next 48 hours with confirmation and payment details.'
            : 'Your booking has been successfully confirmed.'}
        </p>
      </div>

      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Booking details</h2>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Booking ID</span>
            <span className="font-medium">{booking.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Status</span>
            <span className="font-medium capitalize">{booking.status}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Check-in</span>
            <span className="font-medium">{checkIn}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Check-out</span>
            <span className="font-medium">{checkOut}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Nights</span>
            <span className="font-medium">{booking.nights}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total (estimate)</span>
            <span className="font-bold text-lg">R {total}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <Link to="/bookings" className="btn btn-primary flex-1 text-center">
          View my bookings
        </Link>
        <Link to="/" className="btn btn-secondary flex-1 text-center">
          Back to home
        </Link>
      </div>
    </div>
  );
}
